import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiEye } from 'react-icons/fi';

const API_BASE = '/api';

function Customers() {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({ email: '', name: '', stripe_id: '' });
  const [viewingCustomer, setViewingCustomer] = useState(null);
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchCustomers = () => {
    fetch(`${API_BASE}/customers/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setCustomers(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const openAddCustomer = () => {
    setEditingCustomer(null);
    setCustomerForm({ email: '', name: '', stripe_id: '' });
    onModalOpen();
  };

  const openEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({ email: customer.email, name: customer.name || '', stripe_id: customer.stripe_id });
    onModalOpen();
  };

  const openViewCustomer = (customer) => {
    fetch(`${API_BASE}/customers/${customer.id}/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setViewingCustomer(data);
        onViewOpen();
      });
  };

  const handleSaveCustomer = async () => {
    const method = editingCustomer ? 'PUT' : 'POST';
    const url = editingCustomer ? `${API_BASE}/customers/${editingCustomer.id}/` : `${API_BASE}/customers/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(customerForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchCustomers();
        toast({ title: editingCustomer ? 'Customer updated' : 'Customer created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving customer', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving customer', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (customer) => {
    setCustomerToDelete(customer);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!customerToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/customers/${customerToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchCustomers();
        toast({ title: 'Customer deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting customer', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting customer', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setCustomerToDelete(null);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Customers</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddCustomer}>
          Add Customer
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Stripe ID</Th>
              <Th>Subscriptions</Th>
              <Th>Payments</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {customers.map(customer => (
              <Tr key={customer.id}>
                <Td fontWeight="medium">{customer.email}</Td>
                <Td>{customer.name || '-'}</Td>
                <Td><Text fontSize="sm" fontFamily="mono">{customer.stripe_id}</Text></Td>
                <Td><Badge colorScheme="teal">{customer.subscription_count}</Badge></Td>
                <Td><Badge colorScheme="green">{customer.payment_count}</Badge></Td>
                <Td>{new Date(customer.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEye />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openViewCustomer(customer)}
                      aria-label="View"
                    />
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditCustomer(customer)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(customer)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {customers.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No customers found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Email</FormLabel>
              <Input
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="customer@example.com"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                placeholder="Customer Name"
              />
            </FormControl>
            {!editingCustomer && (
              <FormControl>
                <FormLabel>Stripe ID (optional)</FormLabel>
                <Input
                  value={customerForm.stripe_id}
                  onChange={(e) => setCustomerForm({ ...customerForm, stripe_id: e.target.value })}
                  placeholder="cus_..."
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveCustomer}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Customer Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingCustomer && (
              <>
                <Text><strong>Email:</strong> {viewingCustomer.email}</Text>
                <Text><strong>Name:</strong> {viewingCustomer.name || '-'}</Text>
                <Text><strong>Stripe ID:</strong> {viewingCustomer.stripe_id}</Text>
                
                <Heading size="sm" mt={6} mb={2}>Subscriptions</Heading>
                {viewingCustomer.subscriptions?.length > 0 ? (
                  <Table size="sm">
                    <Thead>
                      <Tr><Th>Product</Th><Th>Status</Th><Th>Created</Th></Tr>
                    </Thead>
                    <Tbody>
                      {viewingCustomer.subscriptions.map(s => (
                        <Tr key={s.id}>
                          <Td>{s.product}</Td>
                          <Td><Badge colorScheme={s.status === 'active' ? 'green' : 'gray'}>{s.status}</Badge></Td>
                          <Td>{new Date(s.created_at).toLocaleDateString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : <Text color="gray.500">No subscriptions</Text>}

                <Heading size="sm" mt={6} mb={2}>Recent Payments</Heading>
                {viewingCustomer.payments?.length > 0 ? (
                  <Table size="sm">
                    <Thead>
                      <Tr><Th>Amount</Th><Th>Status</Th><Th>Created</Th></Tr>
                    </Thead>
                    <Tbody>
                      {viewingCustomer.payments.map(p => (
                        <Tr key={p.id}>
                          <Td>{(p.amount / 100).toFixed(2)} {p.currency.toUpperCase()}</Td>
                          <Td><Badge colorScheme={p.status === 'succeeded' ? 'green' : 'gray'}>{p.status}</Badge></Td>
                          <Td>{new Date(p.created_at).toLocaleDateString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : <Text color="gray.500">No payments</Text>}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Customer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete customer <strong>{customerToDelete?.email}</strong>? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Customers;
